<script lang="ts">
import { useTicker } from '@monogrid/vue-boost/core'
import { DOMMapping, useDOMToPlane } from '@monogrid/vue-boost/three'
import { templateRef } from '@vueuse/core'
import { gsap } from 'gsap'
import { BufferGeometry, Mesh, ShaderMaterial } from 'three'
import { ComponentPublicInstance, computed, defineComponent, inject, toValue, watch } from 'vue'

import { colors } from '@/../config/unocss/theme'
import { lenisInjectionKey } from '@/App.vue'
import LayerLinearGrid from '@/components/molecules/LayerLinearGrid.vue'
import { useGlobalReducedMotion } from '@/functions/useGlobalReducedMotion'
import { getMaterialBgPlane } from '@/three/utils/materialLib'
import { threeEnvironmentInjection } from '@/three/WebGLApp.vue'

export default defineComponent({
  name: 'LayerBgPlane',
  components: {
    LayerLinearGrid
  },
  props: {
    order: { type: Number, default: 0 },
    transparent: { type: Boolean, default: false },
    colorMix: { type: Number, default: 0 },
    strength: { type: Number, default: 1 },
    squareSize: { type: Number, default: 4.5 },
    mask: { type: Number, default: 0 },
    masked: { type: Boolean, default: false },
    webgl: { type: Boolean, default: true },
    z: { type: Number, default: undefined }
  },
  setup (props, { attrs, slots, emit }) {
    const environment = inject(threeEnvironmentInjection)
    if (!environment) throw new Error('[LayerBgPlane] A ThreeEnvironment is not provided')
    const lenis = inject(lenisInjectionKey)
    const wrapperComponent = templateRef<HTMLElement | ComponentPublicInstance<typeof LayerLinearGrid>>('el')
    const reducedMotion = useGlobalReducedMotion()
    const material = getMaterialBgPlane({ transparent: props.transparent, masked: props.masked })
    const el = computed(() => (wrapperComponent.value && '$el' in wrapperComponent.value) ? wrapperComponent.value.$el : wrapperComponent.value)
    const { mesh, domMapping } = useDOMToPlane(el, {
      material,
      order: () => props.order,
      subdivisions: 1,
      domMappingOptions: {
        boundingOptions: {
          listenScroll: false
        },
        z: () => props.z
      }
    }) as {mesh: Mesh<BufferGeometry, ShaderMaterial>, domMapping: DOMMapping}

    const meshID = computed(() => mesh.uuid)
    const isWebGLActive = computed(() => !!lenis.instance.value && props.webgl && !reducedMotion.value && domMapping.visible.value)

    const style = computed(() => {
      return {
        opacity: isWebGLActive.value ? 0 : 1,
        [props.masked ? 'color' : 'backgroundColor']: isWebGLActive.value ? 'transparent' : gsap.utils.interpolate(colors.black, colors.blue, props.colorMix)
      }
    })

    function onTick ({ time }) {
      mesh.material.uniforms.uTime.value = time
      mesh.material.uniforms.tTrail.value = environment.globals.cursorTrail.value?.getTarget() || null
      mesh.material.uniforms.uMask.value = toValue(props.mask) || 0
    }

    const { toggle } = useTicker(onTick)

    watch([wrapperComponent, () => props.mask], ([component, mask]) => component?.setProgress?.(mask))

    watch([meshID, isWebGLActive], ([_, webglActive]) => {
      toggle(webglActive)
      mesh.visible = webglActive
    }, { immediate: true })

    watch([meshID, () => props.strength], ([_, strength]) => {
      mesh.material.uniforms.uDitherStrength.value = strength
    }, { immediate: true })

    watch([meshID, () => props.squareSize], ([_, size]) => {
      mesh.material.uniforms.uSquareSize.value = size
    }, { immediate: true })

    watch([meshID, () => lenis.instance.value], ([_, _lenis]) => {
      _lenis?.on('scroll', () => domMapping.update())
    }, { immediate: true })

    watch([meshID, () => props.colorMix], ([_, mix]) => {
      mesh.material.uniforms.uColorMix.value = gsap.utils.clamp(0, 1, mix)
    }, { immediate: true })

    watch([meshID, domMapping.width, domMapping.height], ([_, w, h]) => {
      mesh.material.uniforms.uPlaneSize.value.set(w, h)
    }, { immediate: true })

    watch([meshID, () => toValue(environment.width), () => toValue(environment.height), () => environment.webgl.renderer], ([_, w, h, _renderer]) => {
      if (!_renderer) return
      const dpr = _renderer.getPixelRatio() || 1
      mesh.material.uniforms.uResolution.value.set(w * dpr, h * dpr)
    }, { immediate: true })

    return {
      isWebGLActive,
      style
    }
  }
})
</script>

<template>
  <component
    :is="masked ? 'LayerLinearGrid' :'div'"
    ref="el"
    class="pointer-events-none"
    :style="style"
  />
</template>
